import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface ConfirmRemoveWaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  userFullName: string;
  onConfirm: (removalReason?: string) => Promise<void>;
}

function ConfirmRemoveWaitlistModal({
  isOpen,
  onClose,
  userFullName,
  onConfirm,
}: ConfirmRemoveWaitlistModalProps) {
  const [isWorking, setIsWorking] = useState(false);
  const [removalReason, setRemovalReason] = useState("");

  const handleClose = () => {
    setRemovalReason("");
    onClose();
  };

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm(removalReason.trim() || undefined);
      setRemovalReason("");
      onClose();
    } catch (error) {
      console.error("Failed to remove from waitlist:", error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "24px",
          background: "white",
          boxShadow: "0 10px 25px rgba(0,0,0,.3)",
          width: "calc(100% - 32px)",
          maxWidth: "500px",
          minBlockSize: "fit-content",
          overflowY: "auto",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.3)",
          zIndex: 9999,
        },
      }}
    >
      <h3 className="text-xl font-semibold text-gray-900">
        Remove from Waitlist
      </h3>
      <p className="mt-3 text-sm text-gray-600">
        Are you sure you want to remove <strong>{userFullName}</strong> from the
        waitlist?
      </p>

      <div className="mt-4">
        <Label htmlFor="removalReason">Removal Reason (Optional)</Label>
        <Textarea
          className="mt-2"
          disabled={isWorking}
          id="removalReason"
          onChange={(e) => setRemovalReason(e.target.value)}
          placeholder="Enter reason for removal..."
          rows={3}
          value={removalReason}
        />
        <p className="text-xs text-gray-500 mt-1">
          This will be saved for audit purposes.
        </p>
      </div>

      <div className="mt-6 flex justify-end space-x-3">
        <Button disabled={isWorking} onClick={handleClose} variant="outline">
          Cancel
        </Button>
        <Button disabled={isWorking} onClick={handleConfirm}>
          {isWorking ? "Removing..." : "Remove"}
        </Button>
      </div>
    </Modal>
  );
}

export default ConfirmRemoveWaitlistModal;
